/* eslint-disable */
import React, {useEffect, useState} from 'react';
import {
  PermissionsAndroid,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Svg, {Circle, Defs, RadialGradient, Stop} from 'react-native-svg';
import ScreenWrapper from '../../components/ScreenWrapper';
import AuthButton from '../../components/AuthButton';
import {colors, fonts, radii, spacing} from '../../config/theme';
import PompiLogo from '../../assets/PompiImage.svg';
import DestinationPin from '../../assets/icons/location-green.svg';
import ArrowRight from '../../assets/icons/arrow-right.svg';
import SwapIcon from '../../assets/icons/swap.svg';
import CloseIcon from '../../assets/icons/close.svg';
import Toast from 'react-native-toast-message';
import Geolocation from 'react-native-geolocation-service';
import {useTranslation} from 'react-i18next';
import {getPlaceDetailsByLatLng} from '../../services/auth-service';
import {useUserFuelType} from '../../utility/useUserFuelType';

// A field that looks like an input but opens the place search on tap —
// typing happens on SearchScreen, so it never raises the keyboard here.
const PlaceField = ({
  label,
  value,
  placeholder,
  onPress,
  onClear,
  clearLabel,
  highlight,
  onFieldLayout,
}) => (
  <View style={styles.fieldGroup}>
    <Text style={styles.fieldLabel}>{label}</Text>
    <Pressable
      onPress={onPress}
      onLayout={e => onFieldLayout?.(e.nativeEvent.layout.height)}
      accessibilityRole="button"
      accessibilityLabel={value ? `${label}: ${value}` : label}
      style={({pressed}) => [
        styles.field,
        highlight && styles.fieldHighlight,
        pressed && styles.fieldPressed,
      ]}>
      <Text
        numberOfLines={2}
        style={[styles.fieldText, !value && styles.fieldPlaceholder]}>
        {value || placeholder}
      </Text>
      {value ? (
        <Pressable
          onPress={onClear}
          hitSlop={10}
          accessibilityRole="button"
          accessibilityLabel={clearLabel}
          style={styles.clear}>
          <CloseIcon width={14} height={14} color={colors.textMuted} />
        </Pressable>
      ) : null}
    </Pressable>
  </View>
);

const MapSearch = ({navigation}) => {
  const {t} = useTranslation();
  const [startLocation, setStartLocation] = useState(null);
  const [endLocation, setEndLocation] = useState(null);
  const [locating, setLocating] = useState(true);
  // The user's own fuel (chosen at onboarding, changed in Profile), already
  // converted to what maps/gas-stations-by-fuel accepts.
  const fuelType = useUserFuelType();
  // Fields grow to two lines for long addresses; the route markers and
  // swap button follow their measured heights.
  const [startFieldHeight, setStartFieldHeight] = useState(FIELD_HEIGHT);
  const [endFieldHeight, setEndFieldHeight] = useState(FIELD_HEIGHT);

  const requestLocationPermission = async () => {
    try {
      const granted = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
        {
          title: 'Geolocation Permission',
          message: 'Can we access your location?',
          buttonNeutral: 'Ask Me Later',
          buttonNegative: 'Cancel',
          buttonPositive: 'OK',
        },
      );
      return granted === 'granted';
    } catch (err) {
      return false;
    }
  };

  useEffect(() => {
    const prefillFromCurrentLocation = async () => {
      try {
        if (Platform.OS === 'ios') {
          const auth = await Geolocation.requestAuthorization('whenInUse');
          if (auth !== 'granted') {
            setLocating(false);
            return;
          }
        } else {
          const granted = await requestLocationPermission();
          if (!granted) {
            setLocating(false);
            return;
          }
        }

        Geolocation.getCurrentPosition(
          async position => {
            try {
              const {latitude, longitude} = position.coords;
              const res = await getPlaceDetailsByLatLng({
                lat: latitude,
                lng: longitude,
              });

              if (res?.success) {
                const details = res.data?.data;
                if (details) {
                  // Don't overwrite a start the user already picked.
                  setStartLocation(current =>
                    current ?? {
                      name: details.formatted_address,
                      latitude: details.location?.lat,
                      longitude: details.location?.lng,
                      place_id: details.place_id,
                    },
                  );
                }
              }
            } catch (e) {
              // Silent fail; user can still pick manually
            } finally {
              setLocating(false);
            }
          },
          error => {
            // Silent fail; user can still pick manually
            setLocating(false);
          },
          {enableHighAccuracy: true, timeout: 15000, maximumAge: 10000},
        );
      } catch (e) {
        setLocating(false);
      }
    };

    prefillFromCurrentLocation();
  }, []);

  const handleSearch = () => {
    if (startLocation && endLocation) {
      navigation.navigate('MapPumpSearch', {
        startLocation,
        endLocation,
        fuelType,
      });
    } else {
      Toast.show({
        type: 'error',
        text1: t(`root.Toast.Error.SelectStartAndEndLocation`),
      });
    }
  };

  const pickPlace = onSelectLocation => () =>
    navigation.navigate('SearchScreen', {onSelectLocation});

  const swap = () => {
    setStartLocation(endLocation);
    setEndLocation(startLocation);
  };

  return (
    <ScreenWrapper isBackButton={false} Heading={t(`root.Search.title`)}>
      <View style={styles.container}>
        <View style={styles.intro}>
          <View style={styles.eyebrow}>
            <View style={styles.eyebrowDot} />
            <Text style={styles.eyebrowText}>{t(`root.Search.eyebrow`)}</Text>
          </View>
          <Text style={styles.title} accessibilityRole="header">
            {t(`root.Search.HitRoadSmart`)}
          </Text>
          <Text style={styles.subtitle}>
            {t(`root.Search.EnterStartAndDestination`)}
          </Text>
        </View>

        <View style={styles.illustration}>
          <Svg style={StyleSheet.absoluteFill} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
            <Defs>
              <RadialGradient id="searchGlow" cx="50%" cy="50%" r="50%">
                <Stop offset="0" stopColor={colors.primaryFixed} stopOpacity={0.5} />
                <Stop offset="1" stopColor={colors.primaryFixed} stopOpacity={0} />
              </RadialGradient>
            </Defs>
            <Circle cx={50} cy={50} r={50} fill="url(#searchGlow)" />
          </Svg>
          <PompiLogo width="70%" height="70%" />
        </View>

        <View style={styles.routeCard}>
          <View
            style={[
              styles.track,
              {
                paddingTop: LABEL_BLOCK + (startFieldHeight - 12) / 2,
                paddingBottom: (endFieldHeight - 18) / 2,
              },
            ]}>
            <View style={styles.originRing} />
            <View style={styles.trackLine}>
              {[0, 1, 2, 3, 4].map(i => (
                <View key={i} style={styles.trackDash} />
              ))}
            </View>
            <DestinationPin width={14} height={18} />
          </View>
          <View style={styles.fields}>
            <PlaceField
              label={t(`root.Search.startLabel`)}
              value={startLocation?.name}
              placeholder={
                locating
                  ? t(`root.Search.locating`)
                  : t(`root.Search.startPlaceholder`)
              }
              onPress={pickPlace(setStartLocation)}
              onClear={() => setStartLocation(null)}
              clearLabel={t(`root.Search.clearStart`)}
              onFieldLayout={setStartFieldHeight}
            />
            <PlaceField
              label={t(`root.Search.destinationLabel`)}
              value={endLocation?.name}
              placeholder={t(`root.Search.destinationPlaceholder`)}
              onPress={pickPlace(setEndLocation)}
              onClear={() => setEndLocation(null)}
              clearLabel={t(`root.Search.clearDestination`)}
              highlight={!!startLocation && !endLocation}
              onFieldLayout={setEndFieldHeight}
            />
            {startLocation || endLocation ? (
              <Pressable
                onPress={swap}
                hitSlop={6}
                accessibilityRole="button"
                accessibilityLabel={t(`root.Search.swap`)}
                style={({pressed}) => [
                  styles.swap,
                  {top: LABEL_BLOCK + startFieldHeight - 2},
                  pressed && styles.swapPressed,
                ]}>
                <SwapIcon width={16} height={16} color={colors.primary} />
              </Pressable>
            ) : null}
          </View>
        </View>

        <AuthButton
          variant="primary"
          title={t(`root.Search.Search`)}
          iconRight={<ArrowRight width={16} height={16} color={colors.onPrimary} />}
          onPress={handleSearch}
          style={styles.searchButton}
        />
      </View>
    </ScreenWrapper>
  );
};

const FIELD_HEIGHT = 48;
const LABEL_BLOCK = 22; // label line + gap above each field

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: spacing.margin,
    paddingTop: spacing.xs,
    paddingBottom: spacing.lg,
    backgroundColor: colors.background,
  },
  intro: {
    alignItems: 'center',
  },
  eyebrow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: radii.pill,
    backgroundColor: colors.mintWash,
    borderWidth: 1,
    borderColor: colors.mintLine,
  },
  eyebrowDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.secondary,
  },
  eyebrowText: {
    fontFamily: fonts.semiBold,
    fontSize: 12,
    lineHeight: 16,
    color: colors.brandMid,
  },
  title: {
    fontFamily: fonts.bold,
    fontSize: 26,
    lineHeight: 32,
    letterSpacing: -0.4,
    color: colors.text,
    textAlign: 'center',
    marginTop: 12,
  },
  subtitle: {
    fontFamily: fonts.regular,
    fontSize: 14,
    lineHeight: 20,
    color: colors.textMuted,
    textAlign: 'center',
    marginTop: 6,
    paddingHorizontal: spacing.sm,
  },
  illustration: {
    flex: 1,
    minHeight: 150,
    maxHeight: 240,
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: spacing.md,
  },
  routeCard: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: Platform.OS === 'android' ? colors.border : colors.borderSubtle,
    padding: spacing.lg,
    paddingLeft: spacing.md,
    gap: spacing.md,
    ...Platform.select({
      ios: {
        shadowColor: colors.primary,
        shadowOffset: {width: 0, height: 8},
        shadowOpacity: 0.07,
        shadowRadius: 18,
      },
      default: {},
    }),
  },
  // Markers line up with the middle of each field.
  track: {
    width: 16,
    alignItems: 'center',
  },
  originRing: {
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 2.5,
    borderColor: colors.secondary,
    backgroundColor: colors.surface,
  },
  trackLine: {
    flex: 1,
    justifyContent: 'space-evenly',
    alignItems: 'center',
    paddingVertical: 4,
  },
  trackDash: {
    width: 2,
    height: 4,
    borderRadius: 1,
    backgroundColor: colors.secondary,
  },
  fields: {
    flex: 1,
    gap: 14,
  },
  fieldGroup: {
    gap: 6,
  },
  fieldLabel: {
    fontFamily: fonts.semiBold,
    fontSize: 11,
    lineHeight: 16,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: colors.textSubtle,
  },
  field: {
    minHeight: FIELD_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'transparent',
    backgroundColor: colors.surfaceMuted,
    paddingVertical: 10,
    paddingLeft: 14,
    paddingRight: 44, // room for the clear button and the swap button
  },
  // Nudges the eye to the empty destination once the start is known.
  fieldHighlight: {
    backgroundColor: colors.mintWash,
    borderColor: colors.mintLine,
  },
  fieldPressed: {
    opacity: 0.7,
  },
  fieldText: {
    flex: 1,
    fontFamily: fonts.semiBold,
    fontSize: 14,
    lineHeight: 20,
    color: colors.textStrong,
  },
  fieldPlaceholder: {
    fontFamily: fonts.regular,
    color: colors.textMuted,
  },
  clear: {
    position: 'absolute',
    right: 14,
    padding: 2,
  },
  swap: {
    position: 'absolute',
    right: -6,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  swapPressed: {
    backgroundColor: colors.surfaceMuted,
  },
  searchButton: {
    marginTop: spacing.lg,
  },
});

export default MapSearch;